// src/pages/patient/BookConsultation.js
import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  Calendar, Video, MapPin, User, Search, Star, 
  DollarSign, CheckCircle, AlertCircle, Clock, Award, Baby
} from 'lucide-react';
import patientService from '../../services/patientService';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import { TextArea } from '../../components/ui/TextArea';
import Select from '../../components/ui/Select';
import Alert from '../../components/ui/Alert';
import Modal from '../../components/common/Modal';
import LoadingSpinner from '../../components/common/LoadingSpinner';

const BookConsultation = () => {
  
  const navigate = useNavigate();
  
  // State management
  const [loading, setLoading] = useState(false);
  const [doctors, setDoctors] = useState([]);
  const [dependents, setDependents] = useState([]);
  const [selectedDoctor, setSelectedDoctor] = useState(null);
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [checkingSubscription, setCheckingSubscription] = useState(false);
  
  // Filter states
  const [filters, setFilters] = useState({
    search: '',
    specialization: '',
    consultationType: '',
    availability: 'available',
    minRating: 0,
    sortBy: 'rating'
  });

  // Form states
  const [formData, setFormData] = useState({
    patientType: 'adult', // 'adult' or 'dependent'
    dependentId: '',
    type: 'online',
    symptoms: '',
    preferredTime: '', // Combined date and time
    notes: '',
    attachments: []
  });

  const specializations = [
    'General Physician',
    'Cardiologist',
    'Dermatologist',
    'Pediatrician',
    'Gynecologist',
    'Orthopedic',
    'Neurologist',
    'Psychiatrist',
    'ENT Specialist',
    'Ophthalmologist',
    'Dentist',
    'Urologist',
    'Endocrinologist',
    'Pulmonologist',
    'Rheumatologist'
  ];

  useEffect(() => {
    loadDoctors();
    loadDependents();
  }, [filters.specialization, filters.availability, filters.minRating, filters.sortBy]);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (filters.search !== '') {
        loadDoctors();
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [filters.search]);

  const loadDoctors = async () => {
    try {
      setLoading(true);
      setError('');

      const params = {
        search: filters.search || undefined,
        specialization: filters.specialization || undefined,
        isAvailable: filters.availability === 'available' ? true : undefined,
        minRating: filters.minRating > 0 ? filters.minRating : undefined,
        sortBy: filters.sortBy,
        limit: 50
      };

      const response = await patientService.getDoctors(params);
      
      let doctorsList = response.data?.doctors || response.data?.data?.doctors || [];

      // Filter by consultation type if specified
      if (filters.consultationType === 'online') {
        doctorsList = doctorsList.filter(doc => 
          doc.isAvailableOnline !== false
        );
      }

      setDoctors(doctorsList);
    } catch (error) {
      console.error('Load doctors error:', error);
      setError('Failed to load doctors. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const loadDependents = async () => {
    try {
      const response = await patientService.getDependents();
      if (response.success) {
        setDependents(response.data?.dependents || []);
      }
    } catch (error) {
      console.error('Load dependents error:', error);
      // Don't show error - dependents are optional
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const handleFormChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (error) setError('');
  };

  const handlePatientTypeChange = (type) => {
    setFormData(prev => ({
      ...prev,
      patientType: type,
      dependentId: '' // Reset dependent selection when changing type
    }));
  };

  const openBookingModal = async (doctor) => {
    setSelectedDoctor(doctor);
    setShowBookingModal(true);
    
    // Set default consultation type based on doctor's availability
    if (doctor.isAvailableOnline === false) {
      setFormData(prev => ({ ...prev, type: 'physical' }));
    }

    // Check subscription status if booking free consultation
    if (doctor.consultationFee?.online === 0 || doctor.consultationFee?.physical === 0) {
      await checkConsultationLimit();
    }
  };

  const checkConsultationLimit = async () => {
    try {
      setCheckingSubscription(true);
      const response = await patientService.getDoctors({ checkLimit: true });
    } catch (error) {
      console.error('Subscription check error:', error);
    } finally {
      setCheckingSubscription(false);
    }
  };

  const closeBookingModal = () => {
    setShowBookingModal(false);
    setSelectedDoctor(null);
    setFormData({
      patientType: 'adult',
      dependentId: '',
      type: 'online',
      symptoms: '',
      preferredTime: '',
      notes: '',
      attachments: []
    });
    setError('');
  };

  const handleSubmitBooking = async (e) => {
    e.preventDefault();
    
    if (!selectedDoctor) return;

    // Validation
    if (!formData.symptoms || formData.symptoms.trim().length < 10) {
      setError('Please provide a detailed description of symptoms (at least 10 characters)');
      return;
    }

    if (!formData.preferredTime) {
      setError('Please select a preferred date and time');
      return;
    }

    // Validate dependent selection
    if (formData.patientType === 'dependent') {
      if (!formData.dependentId) {
        setError('Please select which child this consultation is for');
        return;
      }
      
      // Validate dependent still exists
      const dependent = dependents.find(dep => dep._id === formData.dependentId);
      if (!dependent) {
        setError('Selected dependent not found. Please refresh and try again.');
        return;
      }

      // Validate dependent is still under 18
      if (dependent.age >= 18) {
        setError('This dependent is 18 or older. Please book as an adult consultation.');
        return;
      }
    }

    // Validate date is in the future
    const selectedDateTime = new Date(formData.preferredTime);
    if (selectedDateTime < new Date()) {
      setError('Please select a future date and time');
      return;
    }

    try {
      setLoading(true);
      setError('');

      const bookingData = {
        doctorId: selectedDoctor._id,
        type: formData.type,
        patientType: formData.patientType,
        symptoms: formData.symptoms.trim(),
        preferredTime: formData.preferredTime,
        patientNotes: formData.notes.trim(),
        fee: formData.type === 'online' 
          ? selectedDoctor.consultationFee?.online 
          : selectedDoctor.consultationFee?.physical
      };

      // Add dependent ID if booking for child
      if (formData.patientType === 'dependent') {
        bookingData.dependentId = formData.dependentId;
      }

      const response = await patientService.bookConsultation(bookingData);

      if (response.success) {
        const dependentName = formData.patientType === 'dependent' 
          ? dependents.find(d => d._id === formData.dependentId)?.firstName 
          : null;
        
        setSuccess(
          `Consultation booked successfully${dependentName ? ` for ${dependentName}` : ''}! The doctor will respond soon.`
        );
        closeBookingModal();
        
        // Redirect to consultations page after 2 seconds
        setTimeout(() => {
          navigate('/patient/consultations');
        }, 2000);
      } else {
        throw new Error(response.message || 'Failed to book consultation');
      }

    } catch (error) {
      console.error('Book consultation error:', error);
      setError(error.message || 'Failed to book consultation. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const DoctorCard = ({ doctor }) => {
    const consultationFee = formData.type === 'online' 
      ? doctor.consultationFee?.online 
      : doctor.consultationFee?.physical;

    const isAvailableNow = doctor.isAvailableNow || doctor.isActive;
    const canBookOnline = doctor.isAvailableOnline !== false;

    return (
      <div className="bg-white rounded-lg shadow-sm border hover:shadow-md transition-shadow p-6">
        <div className="flex items-start space-x-4">
          {/* Doctor Avatar */}
          <div className="flex-shrink-0">
            <div className="w-20 h-20 rounded-full bg-primary-100 flex items-center justify-center overflow-hidden">
              {doctor.user?.avatar ? (
                <img 
                  src={doctor.user.avatar} 
                  alt={`Dr. ${doctor.firstName}`} 
                  className="w-full h-full object-cover" 
                />
              ) : (
                <User className="w-10 h-10 text-primary-600" />
              )}
            </div>
          </div>

          {/* Doctor Info */}
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  {doctor.title || 'Dr.'} {doctor.firstName} {doctor.lastName}
                </h3>
                <p className="text-sm text-primary-600 font-medium">
                  {doctor.specialization}
                </p>
                {doctor.subSpecialization && doctor.subSpecialization.length > 0 && (
                  <p className="text-xs text-gray-500 mt-1">
                    {doctor.subSpecialization.slice(0, 2).join(', ')}
                  </p>
                )}
              </div>
              <div className="flex flex-col items-end space-y-1">
                {doctor.isVerified && (
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    <CheckCircle className="w-3 h-3 mr-1" />
                    Verified
                  </span>
                )}
                {isAvailableNow && (
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    <Clock className="w-3 h-3 mr-1" />
                    Available
                  </span>
                )}
              </div>
            </div>

            {/* Hospital & Experience */}
            <div className="mt-3 space-y-1">
              {doctor.hospital?.name && (
                <p className="text-sm text-gray-600 flex items-center">
                  <MapPin className="w-3 h-3 mr-1 flex-shrink-0" />
                  <span className="truncate">{doctor.hospital.name}</span>
                </p>
              )}
              <p className="text-sm text-gray-600 flex items-center">
                <Award className="w-3 h-3 mr-1 flex-shrink-0" />
                {doctor.experience} years experience
              </p>
            </div>

            {/* Qualifications */}
            {doctor.qualifications && doctor.qualifications.length > 0 && (
              <div className="mt-2">
                <p className="text-xs text-gray-500">
                  {doctor.qualifications.slice(0, 2).map(q => q.degree || q).join(' • ')}
                </p>
              </div>
            )}

            {/* Languages */}
            {doctor.languages && doctor.languages.length > 0 && (
              <div className="mt-2">
                <p className="text-xs text-gray-500">
                  Languages: {doctor.languages.slice(0, 3).join(', ')}
                </p>
              </div>
            )}

            {/* Rating */}
            <div className="mt-3 flex items-center">
              <div className="flex items-center">
                <Star className="w-4 h-4 text-yellow-400 fill-current" />
                <span className="ml-1 text-sm font-medium text-gray-900">
                  {doctor.rating?.average?.toFixed(1) || 'New'}
                </span>
                <span className="ml-1 text-sm text-gray-500">
                  ({doctor.rating?.count || 0} reviews)
                </span>
              </div>
            </div>

            {/* Consultation Types & Fee */}
            <div className="mt-4 flex items-center justify-between flex-wrap gap-2">
              <div className="flex items-center space-x-2">
                {canBookOnline && (
                  <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-blue-100 text-blue-800">
                    <Video className="w-3 h-3 mr-1" />
                    Online
                  </span>
                )}
                {doctor.isAvailablePhysical !== false && (
                  <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-purple-100 text-purple-800">
                    <MapPin className="w-3 h-3 mr-1" />
                    Physical
                  </span>
                )}
              </div>
              <div className="flex items-center text-sm font-semibold text-gray-900">
                <DollarSign className="w-4 h-4" />
                <span>
                  {consultationFee === 0 ? 'Free' : `KES ${consultationFee?.toLocaleString() || 'N/A'}`}
                </span>
              </div>
            </div>

            {/* About (if available) */}
            {doctor.about && (
              <div className="mt-3">
                <p className="text-sm text-gray-600 line-clamp-2">
                  {doctor.about}
                </p>
              </div>
            )}

            {/* Book Button */}
            <div className="mt-4">
              {doctor.isActive !== false && isAvailableNow ? (
                <Button
                  onClick={() => openBookingModal(doctor)}
                  className="w-full"
                  size="sm"
                >
                  <Calendar className="w-4 h-4 mr-2" />
                  Book Consultation
                </Button>
              ) : (
                <Button
                  disabled
                  variant="outline"
                  className="w-full"
                  size="sm"
                >
                  Currently Unavailable
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Book Consultation</h1>
        <p className="mt-1 text-sm text-gray-500">
          Find and book consultations with verified doctors for yourself or your children
        </p>
      </div>

      {/* Success Alert */}
      {success && (
        <Alert type="success" title="Success" onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* Error Alert */}
      {error && !showBookingModal && (
        <Alert type="error" title="Error" onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Search */}
          <div className="lg:col-span-2">
            <Input
              icon={Search}
              placeholder="Search doctors by name or specialization..."
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
            />
          </div>

          {/* Specialization */}
          <Select
            value={filters.specialization}
            onChange={(e) => handleFilterChange('specialization', e.target.value)}
          >
            <option value="">All Specializations</option>
            {specializations.map(spec => (
              <option key={spec} value={spec}>{spec}</option>
            ))}
          </Select>

          {/* Consultation Type */}
          <Select
            value={filters.consultationType}
            onChange={(e) => handleFilterChange('consultationType', e.target.value)}
          >
            <option value="">All Types</option>
            <option value="online">Online Only</option>
            <option value="physical">Physical Only</option>
          </Select>

          {/* Availability */}
          <Select
            value={filters.availability}
            onChange={(e) => handleFilterChange('availability', e.target.value)}
          >
            <option value="available">Available Now</option>
            <option value="all">All Doctors</option>
          </Select>

          {/* Min Rating */}
          <Select
            value={filters.minRating}
            onChange={(e) => handleFilterChange('minRating', parseFloat(e.target.value))}
          >
            <option value="0">Any Rating</option>
            <option value="3">3+ Stars</option>
            <option value="4">4+ Stars</option>
            <option value="4.5">4.5+ Stars</option>
          </Select>

          {/* Sort By */}
          <Select
            value={filters.sortBy}
            onChange={(e) => handleFilterChange('sortBy', e.target.value)}
          >
            <option value="rating">Highest Rated</option>
            <option value="experience">Most Experienced</option>
            <option value="consultations">Most Consultations</option>
            <option value="fee">Lowest Fee</option>
          </Select>

          {/* Clear Filters */}
          <Button
            variant="outline"
            onClick={() => {
              setFilters({
                search: '',
                specialization: '',
                consultationType: '',
                availability: 'available',
                minRating: 0,
                sortBy: 'rating'
              });
              loadDoctors();
            }}
          >
            Clear Filters
          </Button>
        </div>
      </div>

      {/* Doctors List */}
      <div>
        {loading ? (
          <div className="flex justify-center items-center py-12">
            <LoadingSpinner size="lg" />
          </div>
        ) : doctors.length > 0 ? (
          <>
            <div className="mb-4 text-sm text-gray-600">
              Found {doctors.length} doctor{doctors.length !== 1 ? 's' : ''}
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {doctors.map(doctor => (
                <DoctorCard key={doctor._id} doctor={doctor} />
              ))}
            </div>
          </>
        ) : (
          <div className="text-center py-12 bg-white rounded-lg border">
            <User className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No doctors found</h3>
            <p className="text-gray-500 mb-4">
              Try adjusting your filters or search terms
            </p>
            <Button
              variant="outline"
              onClick={() => {
                setFilters({
                  search: '',
                  specialization: '',
                  consultationType: '',
                  availability: 'available',
                  minRating: 0,
                  sortBy: 'rating'
                });
                loadDoctors();
              }}
            >
              Reset Filters
            </Button>
          </div>
        )}
      </div>

      {/* Booking Modal */}
      {showBookingModal && selectedDoctor && (
        <Modal
          isOpen={showBookingModal}
          onClose={closeBookingModal}
          title={`Book Consultation with ${selectedDoctor.title || 'Dr.'} ${selectedDoctor.firstName} ${selectedDoctor.lastName}`}
          size="lg"
        >
          <form onSubmit={handleSubmitBooking} className="space-y-6">
            {/* Error Alert in Modal */}
            {error && (
              <Alert type="error" onClose={() => setError('')}>
                {error}
              </Alert>
            )}

            {/* Doctor Info Summary */}
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 rounded-full bg-primary-100 flex items-center justify-center overflow-hidden">
                  {selectedDoctor.user?.avatar ? (
                    <img 
                      src={selectedDoctor.user.avatar} 
                      alt={selectedDoctor.firstName} 
                      className="w-full h-full object-cover" 
                    />
                  ) : (
                    <User className="w-6 h-6 text-primary-600" />
                  )}
                </div>
                <div className="flex-1">
                  <p className="font-medium text-gray-900">
                    {selectedDoctor.title || 'Dr.'} {selectedDoctor.firstName} {selectedDoctor.lastName}
                  </p>
                  <p className="text-sm text-gray-600">{selectedDoctor.specialization}</p>
                  <div className="flex items-center mt-1">
                    <Star className="w-3 h-3 text-yellow-400 fill-current" />
                    <span className="ml-1 text-xs text-gray-600">
                      {selectedDoctor.rating?.average?.toFixed(1) || 'New'} ({selectedDoctor.rating?.count || 0} reviews)
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Patient Type Selector */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Who is this consultation for? *
              </label>
              <div className="grid grid-cols-2 gap-4">
                <button
                  type="button"
                  onClick={() => handlePatientTypeChange('adult')}
                  className={`p-4 border-2 rounded-lg text-center transition-all ${
                    formData.patientType === 'adult'
                      ? 'border-primary-500 bg-primary-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <User className="w-6 h-6 mx-auto mb-2 text-primary-600" />
                  <p className="font-medium">Myself</p>
                  <p className="text-sm text-gray-600">Adult consultation</p>
                </button>
                <button
                  type="button"
                  onClick={() => handlePatientTypeChange('dependent')}
                  className={`p-4 border-2 rounded-lg text-center transition-all ${
                    formData.patientType === 'dependent'
                      ? 'border-primary-500 bg-primary-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <Baby className="w-6 h-6 mx-auto mb-2 text-primary-600" />
                  <p className="font-medium">My Child</p>
                  <p className="text-sm text-gray-600">Pediatric consultation</p>
                </button>
              </div>
            </div>

            {/* Dependent Selector */}
            {formData.patientType === 'dependent' && (
              <div>
                <label htmlFor="dependentId" className="block text-sm font-medium text-gray-700 mb-2">
                  Select Child *
                </label>
                {dependents.length === 0 ? (
                  <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <p className="text-sm text-yellow-800 mb-2">
                      You haven't added any dependents yet.
                    </p>
                    <Link
                      to="/patient/dependents"
                      className="text-sm text-blue-600 underline hover:text-blue-800"
                    >
                      Add a dependent first →
                    </Link>
                  </div>
                ) : (
                  <>
                    <Select
                      id="dependentId"
                      value={formData.dependentId}
                      onChange={(e) => handleFormChange('dependentId', e.target.value)}
                      required
                    >
                      <option value="">Select your child</option>
                      {dependents.map(dep => (
                        <option key={dep._id} value={dep._id}>
                          {dep.firstName} {dep.lastName} ({dep.age} years old)
                        </option>
                      ))}
                    </Select>
                    <p className="mt-1 text-xs text-gray-500">
                      Parental consent will be automatically recorded
                    </p>
                  </>
                )}
              </div>
            )}

            {/* Consultation Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Consultation Type *
              </label>
              <div className="grid grid-cols-2 gap-4">
                {selectedDoctor.isAvailableOnline !== false && (
                  <button
                    type="button"
                    onClick={() => handleFormChange('type', 'online')}
                    className={`p-4 border-2 rounded-lg text-center transition-all ${
                      formData.type === 'online'
                        ? 'border-primary-500 bg-primary-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <Video className="w-6 h-6 mx-auto mb-2 text-primary-600" />
                    <p className="font-medium">Online</p>
                    <p className="text-sm text-gray-600">
                      {selectedDoctor.consultationFee?.online === 0 
                        ? 'Free' 
                        : `KES ${selectedDoctor.consultationFee?.online?.toLocaleString()}`}
                    </p>
                  </button>
                )}
                {selectedDoctor.isAvailablePhysical !== false && (
                  <button
                    type="button"
                    onClick={() => handleFormChange('type', 'physical')}
                    className={`p-4 border-2 rounded-lg text-center transition-all ${
                      formData.type === 'physical'
                        ? 'border-primary-500 bg-primary-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <MapPin className="w-6 h-6 mx-auto mb-2 text-primary-600" />
                    <p className="font-medium">Physical</p>
                    <p className="text-sm text-gray-600">
                      {selectedDoctor.consultationFee?.physical === 0 
                        ? 'Free' 
                        : `KES ${selectedDoctor.consultationFee?.physical?.toLocaleString()}`}
                    </p>
                  </button>
                )}
              </div>
            </div>

            {/* Symptoms */}
            <div>
              <label htmlFor="symptoms" className="block text-sm font-medium text-gray-700 mb-2">
                Symptoms / Reason for Consultation *
              </label>
              <TextArea
                id="symptoms"
                rows={4}
                placeholder="Please describe the symptoms in detail..."
                value={formData.symptoms}
                onChange={(e) => handleFormChange('symptoms', e.target.value)}
                required
              />
              <p className="mt-1 text-xs text-gray-500">
                Minimum 10 characters required
              </p>
            </div>

            {/* Preferred Date & Time */}
            <div>
              <label htmlFor="preferredTime" className="block text-sm font-medium text-gray-700 mb-2">
                Preferred Date & Time *
              </label>
              <Input
                id="preferredTime"
                type="datetime-local"
                min={new Date().toISOString().slice(0, 16)}
                value={formData.preferredTime}
                onChange={(e) => handleFormChange('preferredTime', e.target.value)}
                required
              />
              <p className="mt-1 text-xs text-gray-500">
                Select your preferred consultation date and time
              </p>
            </div>

            {/* Additional Notes */}
            <div>
              <label htmlFor="notes" className="block text-sm font-medium text-gray-700 mb-2">
                Additional Notes (Optional)
              </label>
              <TextArea
                id="notes"
                rows={3}
                placeholder="Any additional information you'd like to share..."
                value={formData.notes}
                onChange={(e) => handleFormChange('notes', e.target.value)}
              />
            </div>

            {/* Info Note */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex">
                <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <div className="ml-3">
                  <h4 className="text-sm font-medium text-blue-900">Important Information</h4>
                  <ul className="mt-1 text-sm text-blue-700 space-y-1">
                    <li>• The doctor will review your consultation request</li>
                    <li>• You'll receive a notification once the doctor responds</li>
                    <li>• Payment will be processed after the doctor accepts</li>
                    {formData.patientType === 'dependent' && (
                      <li>• Parental consent is automatically recorded for pediatric consultations</li>
                    )}
                  </ul>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-end space-x-3 pt-4 border-t">
              <Button
                type="button"
                variant="outline"
                onClick={closeBookingModal}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={loading || checkingSubscription || (formData.patientType === 'dependent' && dependents.length === 0)}
                loading={loading}
              >
                {loading ? 'Booking...' : 'Book Consultation'}
              </Button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
};

export default BookConsultation;